import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IonContent, IonPage } from "@ionic/react";
import {
  ArrowLeft,
  VideoIcon,
  PhoneIcon,
  UserIcon,
  HomeIcon,
  HospitalIcon,
  Check,
} from "lucide-react";
import { routeForServiceMode } from "../utils/routeByMode";
import { setStateFromPayload } from "../navigoSlice";
import BookingDetailsSheet from "./BookingDetailsSheet";

function modeIcon(modeName) {
  const props = { size: 22, style: { color: "#97144D", flexShrink: 0 } };
  switch (modeName) {
    case "Video consultation":
      return <VideoIcon {...props} />;
    case "Tele consultation":
      return <PhoneIcon {...props} />;
    case "In-person consultation":
      return <UserIcon {...props} />;
    case "Home sample":
      return <HomeIcon {...props} />;
    case "Centre visit":
      return <HospitalIcon {...props} />;
    default:
      return <HospitalIcon {...props} />;
  }
}

export default function SelectModePage() {
  const { state } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile,
  );

  const service = state?.service;
  const planId = state?.planId;
  const pageType = state?.pageType;
  const modes = service?.serviceModeDetails || [];

  const [selectedIndex, setSelectedIndex] = useState(null);

  // Seed the booking summary with the service so the floater shows it while the
  // user is still choosing a mode (mode stays hidden until Continue commits it).
  useEffect(() => {
    dispatch(
      setStateFromPayload({
        currentServices: service,
        selectedBookingMode: "",
        familyMemberSelectedData: {},
        addressSelected: null,
      }),
    );
  }, []);

  const handleContinue = () => {
    if (selectedIndex == null) return;
    const chosen = modes[selectedIndex];
    // Downstream screens operate on serviceModeDetails[0], so move the chosen
    // mode to the front before routing.
    const reordered = [chosen, ...modes.filter((_, i) => i !== selectedIndex)];
    routeForServiceMode({
      service: { ...service, serviceModeDetails: reordered },
      modeName: chosen.modeName,
      planId,
      pageType,
      patientId: patientProfile.id,
      dispatch,
      history,
    });
  };

  return (
    <IonPage>
      <IonContent>
        <div
          style={{
            fontFamily: "'Montserrat', sans-serif",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "16px",
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <button
              type="button"
              onClick={() => history.goBack()}
              aria-label="Back"
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#97144D",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ArrowLeft size={22} />
            </button>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#12194C",
              }}
            >
              Select booking mode
            </h2>
          </div>

          {service?.name && (
            <p
              style={{
                margin: "16px 16px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#6B7280",
              }}
            >
              {service.name}
            </p>
          )}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "12px 16px 16px",
            }}
          >
            {modes.map((mode, index) => {
              const selected = selectedIndex === index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 14,
                    marginBottom: 12,
                    border: `1.5px solid ${selected ? "#97144D" : "#E5E7EB"}`,
                    borderRadius: 12,
                    backgroundColor: selected ? "#FBE7ED" : "#FFFFFF",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 10,
                      backgroundColor: selected ? "#FFFFFF" : "#F1F5F9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {modeIcon(mode.modeName)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#12194C",
                      }}
                    >
                      {mode.modeName}
                    </p>
                    {(mode.discountedPrice != null ||
                      mode.maxPrice != null) && (
                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        {mode.discountedPrice === 0 && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 10px",
                              backgroundColor: "#DCFCE7",
                              color: "#15803D",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            <Check size={13} strokeWidth={3} />
                            Sponsored
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#12194C",
                          }}
                        >
                          ₹{mode.discountedPrice}
                        </span>
                        {mode.maxPrice ? (
                          <span
                            style={{
                              fontSize: 13,
                              color: "#9CA3AF",
                              textDecoration: "line-through",
                            }}
                          >
                            ₹{mode.maxPrice}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <span
                    style={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      borderRadius: "50%",
                      border: `2px solid ${selected ? "#97144D" : "#D1D5DB"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {selected && (
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#97144D",
                        }}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              padding: 16,
              borderTop: "1px solid #F1F5F9",
              marginBottom: "calc(54px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <button
              type="button"
              onClick={handleContinue}
              disabled={selectedIndex == null}
              style={{
                width: "100%",
                padding: "14px 16px",
                backgroundColor: selectedIndex == null ? "#D1D5DB" : "#97144D",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 700,
                cursor: selectedIndex == null ? "default" : "pointer",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Continue
            </button>
          </div>
        </div>
        <BookingDetailsSheet />
      </IonContent>
    </IonPage>
  );
}
